/**
 * Advanced Pose Analysis Utilities
 *
 * Provides sophisticated analysis of MediaPipe pose landmarks for:
 * - Orientation detection (supine/prone/side)
 * - Body part cutoff detection
 * - Framing quality assessment
 * - Message debouncing to prevent flickering
 */

import { NormalizedLandmark } from '@mediapipe/tasks-vision';
import { GuidanceMessage } from '../context/RecordingContext';

export type InfantOrientation = 'supine' | 'prone' | 'side' | 'unknown';
export type FramingQuality = 'good' | 'needs_adjustment' | 'poor';

interface PoseAnalysisResult {
  orientation: InfantOrientation;
  framingQuality: FramingQuality;
  messages: GuidanceMessage[];
  boundingBox: {
    xMin: number;
    xMax: number;
    yMin: number;
    yMax: number;
    width: number;
    height: number;
  };
  bodyCutoff: {
    head: boolean;
    leftArm: boolean;
    rightArm: boolean;
    leftLeg: boolean;
    rightLeg: boolean;
  };
}

/**
 * Analyze pose landmarks for comprehensive guidance
 */
export function analyzePose(landmarks: NormalizedLandmark[]): PoseAnalysisResult {
  const boundingBox = calculateBoundingBox(landmarks);
  const orientation = detectOrientation(landmarks);
  const bodyCutoff = detectBodyCutoff(landmarks, boundingBox);
  const framingQuality = assessFramingQuality(boundingBox, bodyCutoff);
  const messages = generateGuidanceMessages(framingQuality, boundingBox, bodyCutoff, orientation);

  return {
    orientation,
    framingQuality,
    messages,
    boundingBox,
    bodyCutoff
  };
}

/**
 * Calculate bounding box around detected pose
 */
function calculateBoundingBox(landmarks: NormalizedLandmark[]) {
  const visibleLandmarks = landmarks.filter(l => l.visibility && l.visibility > 0.5);

  if (visibleLandmarks.length === 0) {
    return { xMin: 0, xMax: 1, yMin: 0, yMax: 1, width: 1, height: 1 };
  }

  const xs = visibleLandmarks.map(l => l.x);
  const ys = visibleLandmarks.map(l => l.y);

  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);

  return {
    xMin,
    xMax,
    yMin,
    yMax,
    width: xMax - xMin,
    height: yMax - yMin
  };
}

/**
 * Detect infant orientation (supine/prone/side)
 * Based on relative positions of shoulders, hips, and head
 */
function detectOrientation(landmarks: NormalizedLandmark[]): InfantOrientation {
  // Key landmarks for orientation detection
  const nose = landmarks[0];
  const leftShoulder = landmarks[11];
  const rightShoulder = landmarks[12];
  const leftHip = landmarks[23];
  const rightHip = landmarks[24];

  if (!nose || !leftShoulder || !rightShoulder || !leftHip || !rightHip) {
    return 'unknown';
  }

  // Calculate shoulder and hip widths
  const shoulderWidth = Math.abs(leftShoulder.x - rightShoulder.x);
  const hipWidth = Math.abs(leftHip.x - rightHip.x);

  // Calculate vertical positions
  const shoulderY = (leftShoulder.y + rightShoulder.y) / 2;
  const hipY = (leftHip.y + rightHip.y) / 2;

  // Supine (on back): wide shoulders/hips, face visible
  if (shoulderWidth > 0.15 && hipWidth > 0.15) {
    return 'supine';
  }

  // Prone (on stomach): narrow shoulders/hips, face might not be visible
  if (shoulderWidth < 0.08 || hipWidth < 0.08) {
    return 'prone';
  }

  // Side: one shoulder significantly higher than the other
  const shoulderHeightDiff = Math.abs(leftShoulder.y - rightShoulder.y);
  if (shoulderHeightDiff > 0.1) {
    return 'side';
  }

  return 'unknown';
}

/**
 * Detect which body parts are cut off by frame edges
 */
function detectBodyCutoff(landmarks: NormalizedLandmark[], boundingBox: any) {
  const margin = 0.05; // 5% margin from edges

  // Check if specific body parts are near frame edges
  const headLandmarks = landmarks.slice(0, 11);
  const leftArmLandmarks = [landmarks[11], landmarks[13], landmarks[15]];
  const rightArmLandmarks = [landmarks[12], landmarks[14], landmarks[16]];
  const leftLegLandmarks = [landmarks[23], landmarks[25], landmarks[27]];
  const rightLegLandmarks = [landmarks[24], landmarks[26], landmarks[28]];

  const isNearEdge = (landmark: NormalizedLandmark) => {
    if (!landmark) return false;
    return landmark.x < margin || landmark.x > (1 - margin) ||
           landmark.y < margin || landmark.y > (1 - margin);
  };

  return {
    head: headLandmarks.some(l => l && isNearEdge(l)),
    leftArm: leftArmLandmarks.some(l => l && isNearEdge(l)),
    rightArm: rightArmLandmarks.some(l => l && isNearEdge(l)),
    leftLeg: leftLegLandmarks.some(l => l && isNearEdge(l)),
    rightLeg: rightLegLandmarks.some(l => l && isNearEdge(l))
  };
}

/**
 * Assess overall framing quality
 */
function assessFramingQuality(boundingBox: any, bodyCutoff: any): FramingQuality {
  const { width, height } = boundingBox;

  // Check if any critical body parts are cut off
  const hasCutoff = bodyCutoff.head || bodyCutoff.leftArm || bodyCutoff.rightArm ||
                    bodyCutoff.leftLeg || bodyCutoff.rightLeg;

  if (hasCutoff) {
    return 'poor';
  }

  // Too close: takes up > 85% of frame
  if (width > 0.85 || height > 0.85) {
    return 'needs_adjustment';
  }

  // Too far: takes up < 25% of frame
  if (width < 0.25 || height < 0.25) {
    return 'needs_adjustment';
  }

  // Good framing: 25-85% of frame, no cutoff
  return 'good';
}

/**
 * Generate guidance messages based on analysis
 */
function generateGuidanceMessages(
  quality: FramingQuality,
  boundingBox: any,
  bodyCutoff: any,
  orientation: InfantOrientation
): GuidanceMessage[] {
  const messages: GuidanceMessage[] = [];

  // Priority 1: Body cutoff (most critical)
  if (bodyCutoff.head || bodyCutoff.leftArm || bodyCutoff.rightArm ||
      bodyCutoff.leftLeg || bodyCutoff.rightLeg) {
    const cutoffParts = [];
    if (bodyCutoff.head) cutoffParts.push('head');
    if (bodyCutoff.leftArm || bodyCutoff.rightArm) cutoffParts.push('arms');
    if (bodyCutoff.leftLeg || bodyCutoff.rightLeg) cutoffParts.push('legs');

    messages.push({
      type: 'error',
      message: `Parts of baby are cut off - adjust angle`,
      priority: 1
    });
    return messages; // Return immediately for critical issue
  }

  // Priority 2: Distance issues
  if (boundingBox.width > 0.85 || boundingBox.height > 0.85) {
    messages.push({
      type: 'warning',
      message: 'Camera too close - pull back',
      priority: 2
    });
  } else if (boundingBox.width < 0.25 || boundingBox.height < 0.25) {
    messages.push({
      type: 'info',
      message: 'Camera too far - move closer',
      priority: 3
    });
  }

  // Priority 3: Good positioning
  if (messages.length === 0) {
    messages.push({
      type: 'success',
      message: 'Great positioning!',
      priority: 4
    });
  }

  return messages;
}

/**
 * Message Debouncer
 * Prevents flickering by only updating messages if they persist for a minimum duration
 */
export class MessageDebouncer {
  private lastMessage: string | null = null;
  private messageTimestamp: number = 0;
  private readonly debounceTime: number;

  constructor(debounceTimeMs: number = 1000) {
    this.debounceTime = debounceTimeMs;
  }

  /**
   * Check if message should be displayed
   * Returns true if message has persisted long enough
   */
  shouldUpdate(newMessage: string): boolean {
    const now = Date.now();

    if (newMessage !== this.lastMessage) {
      // New message - start debounce timer
      this.lastMessage = newMessage;
      this.messageTimestamp = now;
      return false; // Don't update yet
    }

    // Same message - check if enough time has passed
    const elapsed = now - this.messageTimestamp;
    return elapsed >= this.debounceTime;
  }

  reset(): void {
    this.lastMessage = null;
    this.messageTimestamp = 0;
  }
}

/**
 * Message Smoother
 * Averages framing quality over multiple frames to reduce jitter
 */
export class QualitySmoother {
  private readonly history: FramingQuality[] = [];
  private readonly maxHistory: number;

  constructor(historySize: number = 5) {
    this.maxHistory = historySize;
  }

  add(quality: FramingQuality): FramingQuality {
    this.history.push(quality);
    if (this.history.length > this.maxHistory) {
      this.history.shift();
    }

    // Return most common quality in history
    const counts: Record<FramingQuality, number> = {
      'good': 0,
      'needs_adjustment': 0,
      'poor': 0
    };

    this.history.forEach(q => counts[q]++);

    const mostCommon = Object.entries(counts).reduce((a, b) =>
      a[1] > b[1] ? a : b
    )[0] as FramingQuality;

    return mostCommon;
  }

  reset(): void {
    this.history.length = 0;
  }
}
